import { Key, ReactNode } from 'react';
import { MdInfoOutline } from 'react-icons/md';
import { Button, cn, Image, TmdxAvatar, Tooltip } from '@ds';
import { ResourceRowData } from '@features/components/CaseOverview/components/AssignedResources/types';
import {
  getColorsByOrganType,
  getOrganImg,
  getRolesFormatted,
  getContactFullName,
  displayPhoneNumber,
} from '@/utils/helpers';
import { CustomerRole, CustomerRoleT } from '@/api/apis/customer';
import { columns } from './config';
import { getLastAssignedOPOContactTooltipMessage, getLastAssignedTooltipMessage } from './utils';

export const getRenderResourceTableCell =
  (onAssign: (resource: ResourceRowData) => void, selectedRole?: CustomerRoleT) =>
  (resource: ResourceRowData & { isLastAssigned?: boolean }, columnKey: Key): ReactNode => {
    const {
      firstName,
      lastName,
      primaryPhone,
      secondaryPhone,
      photoPath,
      assigned,
      canDetach = true,
      canAssign = true,
      roles,
      organSpecializations,
      baseLocation,
      homeLocation,
      displayOpoAffiliationInNameColumn,
      affiliationOpoName,
      isLastAssigned,
    } = resource;

    const assignBtnClasses = assigned ? 'text-danger-30 border-danger-30' : '';
    const assignBtnDisabled = assigned ? !canDetach : !canAssign;

    const onUserAssign = () => {
      onAssign(resource);
    };

    switch (columnKey) {
      case columns.name.key:
        return (
          <div className="flex items-center gap-2">
            <div className="flex flex-col w-14 max-w-14 shrink-0 items-center overflow-hidden">
              <TmdxAvatar
                className="shrink-0"
                size="lg"
                radius="lg"
                name={getContactFullName(firstName, lastName)}
                src={photoPath}
              />
            </div>
            <div>
              <div className="text-m-m">{getContactFullName(firstName, lastName)}</div>
              <div>
                {displayOpoAffiliationInNameColumn && affiliationOpoName
                  ? affiliationOpoName
                  : getRolesFormatted(roles)}
              </div>
            </div>
          </div>
        );
      case columns.phone.key:
        return <div>{displayPhoneNumber(primaryPhone)}</div>;
      case columns.primaryPhone.key:
        return <div>{displayPhoneNumber(primaryPhone)}</div>;
      case columns.secondaryPhone.key:
        return <div>{displayPhoneNumber(secondaryPhone)}</div>;
      case columns.organ.key:
        return (
          <div className="flex flex-row items-center gap-0.5">
            {organSpecializations?.map((organ) => {
              const colors = getColorsByOrganType(organ);
              return (
                <div key={organ} className={`${colors.bg50} w-6 h-6 rounded-full flex justify-center items-center`}>
                  <Image src={getOrganImg(organ)} alt={organ} />
                </div>
              );
            })}
          </div>
        );
      case columns.baseLocation.key:
        return <div className="truncate">{baseLocation?.name || ''}</div>;
      case columns.homeLocation.key:
        return <div className="truncate">{homeLocation}</div>;
      case columns.assign.key: {
        let tooltip = '';
        if (isLastAssigned) {
          tooltip = affiliationOpoName
            ? getLastAssignedOPOContactTooltipMessage()
            : getLastAssignedTooltipMessage(selectedRole!);
        }
        const btn = (
          <div className="flex w-full justify-end">
            <Button
              variant="outline"
              className={cn('flex flex-row items-center text-s-sb h-8 w-20', assignBtnClasses, {
                isLastAssigned: 'text-danger-30 border-danger-30',
              })}
              onClick={onUserAssign}
              isDisabled={assignBtnDisabled || (isLastAssigned && selectedRole !== CustomerRole.TxCenterAdministrator)}
            >
              {isLastAssigned && selectedRole !== CustomerRole.TxCenterAdministrator && (
                <span className="m-0 p-0">
                  <MdInfoOutline size={18} className="text-danger-30" />
                </span>
              )}
              {assigned ? 'Detach' : 'Assign'}
            </Button>
          </div>
        );
        return isLastAssigned && tooltip ? (
          <Tooltip content={tooltip} placement="top">
            <span>{btn}</span>
          </Tooltip>
        ) : (
          btn
        );
      }
      default:
        return null;
    }
  };
